import React, { useState } from 'react';
import { MapPinIcon } from 'lucide-react';
import { usePower } from '../../contexts/PowerContext';
import { useAccount } from '../../contexts/AccountContext';
import { PageHeader } from '../../components/ui/PageHeader';
import { BeforeYouGo } from '../../components/power/BeforeYouGo';
import { PowerOverview } from '../../components/power/PowerOverview';
import { PowerMap } from '../../components/power/PowerMap';
import { HowItWorks, PowerNearby, PowerReliabilityPanel, QuickActions, RecentReports, YourImpact } from '../../components/power/PowerPanels';
import { ReportPowerSheet } from '../../components/power/ReportPowerSheet';
import { powerIcons } from '../../components/power/PowerPrimitives';

/**
 * Campuzon Power.
 *
 * The hierarchy is deliberate: Before you go answers the question the person came
 * with, the overview and quick actions sit beside it, and everything below is
 * supporting evidence. Nothing on this page is equal in weight to the top card.
 */
export function Power() {
  const {
    campusLocationId,
    next,
    offline,
    setOffline,
    subscribed,
    toggleSubscription
  } = usePower();
  const {
    campusName
  } = useAccount();
  const [reporting, setReporting] = useState(false);
  const [reportLocation, setReportLocation] = useState<string | null>(null);
  const [selected, setSelected] = useState<string | null>(next?.locationId ?? null);
  const openReport = (locationId?: string) => {
    setReportLocation(locationId ?? selected ?? campusLocationId);
    setReporting(true);
  };
  const Zap = powerIcons.power;
  return <div className="space-y-5" data-id="element-11529" data-mp="38t4yei4vr3m">
      <PageHeader eyebrow="Campuzon Power" title="Power" description="Know where the light is before you move." documentTitle actions={<span className="inline-flex min-h-touch items-center gap-2 rounded-xl border border-line bg-surface px-3.5 text-body-sm font-medium text-ink" data-id="element-11531" data-mp="2ixmhgie4vyt0">
            <MapPinIcon className="h-4 w-4 text-ink-muted" aria-hidden data-id="element-11532" data-mp="2e02r4k45vvkp" />
            {campusName}
          </span>} data-id="element-11530" data-mp="h6rg7b15r7ew" />

      {/* Before you go leads, with the overview and quick actions beside it on
          wide screens and beneath it on narrow ones. */}
      <div className="grid gap-4 xl:grid-cols-[minmax(0,1.05fr)_minmax(0,1.25fr)_minmax(0,0.8fr)]" data-id="element-11533" data-mp="rtb52zlylhl9">
        <BeforeYouGo data-id="element-11534" data-mp="vvehprkd6s84" />
        <PowerOverview onReport={openReport} data-id="element-11535" data-mp="vvxocc4kdu6u" />
        <QuickActions onReport={() => openReport()} onNotify={() => toggleSubscription(campusLocationId)} notifying={subscribed(campusLocationId)} data-id="element-11536" data-mp="10u8xekn1c0o0" />
      </div>

      <div className="grid gap-4 xl:grid-cols-[minmax(0,1.6fr)_minmax(0,1fr)_minmax(0,1fr)]" data-id="element-11537" data-mp="1ak28lkj9mdrq">
        <PowerMap selectedId={selected} onSelect={setSelected} data-id="element-11538" data-mp="1fj5grdujl03x" />
        <PowerNearby data-id="element-11539" data-mp="1fim000gmohrh" />
        <RecentReports data-id="element-11540" data-mp="1fkycvz7j41xo" />
      </div>

      <div className="grid gap-4 lg:grid-cols-3" data-id="element-11541" data-mp="1jl8r1z5h5ynm">
        <PowerReliabilityPanel locationId={selected ?? campusLocationId} data-id="element-11542" data-mp="1jinot919lgpc" />
        <HowItWorks data-id="element-11543" data-mp="1oklgmrnqdjtw" />
        <YourImpact onReport={() => openReport()} data-id="element-11544" data-mp="1ok3e5dah69dj" />
      </div>

      {/* Connectivity is a real state in this product, so it is reachable rather
          than theoretical: cached power data must never read as live. */}
      <p className="flex flex-wrap items-center gap-2 text-caption text-ink-subtle" data-id="element-11545" data-mp="301677b3ly4w8">
        <Zap className="h-3.5 w-3.5" aria-hidden data-id="element-11546" data-mp="303ac9wd8t9y3" />
        Power information is community-reported and combined with campus and property
        sources. It is guidance, not a guarantee.
        <button type="button" onClick={() => setOffline(!offline)} className="min-h-touch rounded-lg border border-line px-3 text-caption font-medium text-ink-muted transition-colors duration-150 ease-out hover:text-ink" data-id="element-11547" data-mp="3rrkdal5dqcxc">
          {offline ? 'Reconnect' : 'Simulate losing connection'}
        </button>
      </p>

      <ReportPowerSheet open={reporting} onClose={() => setReporting(false)} presetLocationId={reportLocation} data-id="element-11548" data-mp="fb72dahv0bxu" />
    </div>;
}